import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';

import { Link, DefaultLink } from 'hb-react/frontoffice/components/Link';
import {
  isFileExternalSource,
  isFileAnArticle,
} from 'hb-react/shared/utils/files/fileContentTypes';

import FileThumbnailContent from './components/FileThumbnailContent';
import FileThumbnailReactions from './components/FileThumbnailReactions';
import ThumbnailBottom from '../components/ThumbnailBottom';
import ThumbnailContent from '../components/ThumbnailContent';
import ThumbnailHeader from '../components/ThumbnailHeader';

const FileThumbnail = ({
  bottom,
  content,
  header,
  link,
  reactions,
  showReactions = true,
  tag,
}) => (
  <Card
    flexDirection="column"
    width="100%"
    px={0}
    pb={3}
    pt={0}
    data-testid="file-thumbnail"
  >
    <ThumbnailHeader {...header} />
    <ThumbnailContent tag={tag}>
      <FileThumbnailContent {...content} />
    </ThumbnailContent>
    <ThumbnailBottomLink {...link}>
      <ThumbnailBottom {...bottom} />
    </ThumbnailBottomLink>
    {reactions && showReactions && <FileThumbnailReactions {...reactions} />}
  </Card>
);

// Description of allowed properties for each shape
FileThumbnail.propTypes = {
  bottom: PropTypes.shape({
    isFile: PropTypes.bool,
    text: PropTypes.string,
  }),
  content: PropTypes.shape({
    file: PropTypes.object.isRequired,
  }).isRequired,
  header: PropTypes.shape({
    actions: PropTypes.array,
    date: PropTypes.string.isRequired,
    isFolder: PropTypes.bool,
    owner: PropTypes.shape({
      name: PropTypes.string,
      photo: PropTypes.string,
    }),
    to: PropTypes.object,
    withUserInformations: PropTypes.bool,
  }),
  link: PropTypes.shape({
    fileType: PropTypes.string,
    fileUrl: PropTypes.string,
    to: PropTypes.object,
    url: PropTypes.string,
  }),
  reactions: PropTypes.shape({
    comments: PropTypes.shape({
      ariaExpanded: PropTypes.bool,
      dataTestId: PropTypes.string,
      showFullText: PropTypes.bool,
      onClick: PropTypes.func.isRequired,
      totalComments: PropTypes.number,
    }),
    likes: PropTypes.shape({
      initialLikes: PropTypes.shape({
        count: PropTypes.number,
        id: PropTypes.number,
        likeable_id: PropTypes.number,
        users: PropTypes.array,
      }).isRequired,
      onTogglePostLike: PropTypes.func.isRequired,
      type: PropTypes.string.isRequired,
      showFullText: PropTypes.bool,
      measure: PropTypes.func,
      onLikeCallback: PropTypes.func,
    }),
  }),
  showReactions: PropTypes.bool,
  tag: PropTypes.string.isRequired,
};

export default FileThumbnail;

export const ThumbnailBottomLink = ({
  children,
  fileType,
  url,
  fileUrl,
  to = {},
}) => {
  if (isFileAnArticle(fileType)) {
    return (
      <LinkStyled>
        <DefaultLink href={url} target="_blank">
          {children}
        </DefaultLink>
      </LinkStyled>
    );
  }

  if (isFileExternalSource(fileType)) {
    return (
      <LinkStyled>
        <DefaultLink
          href={url}
          target="_blank"
          data-testid="thumbnail-external-link"
        >
          {children}
        </DefaultLink>
      </LinkStyled>
    );
  }

  return (
    <LinkStyled>
      <Link to={fileUrl || to} data-testid="thumbnail-link">
        {children}
      </Link>
    </LinkStyled>
  );
};

ThumbnailBottomLink.propTypes = {
  children: PropTypes.node.isRequired,
  fileType: PropTypes.string,
  fileUrl: PropTypes.string,
  to: PropTypes.object,
  url: PropTypes.string,
};

const LinkStyled = styled(Box).attrs({ display: 'block' })`
  :hover div {
    color: ${({ theme }) => theme.colors.link};
    text-decoration: underline;
  }
`;
